// src/features/blog/pages/blog-list-page.tsx
import { Suspense, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { Container } from "../../../shared/ui/container";
import { BlogCard, blogReadMinutes } from "../../../shared/ui/blog-card";
import { ImageWithFallback } from "../../../shared/ui/image-with-fallback";
import { Pagination } from "../../../shared/ui/pagination";
import { blogListFilters, useBlogs } from "../hooks/use-blogs";
import { useRecentPosts } from "../hooks/use-recent-posts";
import { BreadcrumbLd, siteUrl } from "../../../shared/seo/breadcrumb-ld";
import { PageSeo, SITE_ORIGIN } from "../../../shared/seo/page-seo";
import { parseLang, type Lang } from "@majourneys/shared";

import { Icon } from "@majourneys/icons";
interface BlogPost {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  image?: string;
  date: string;
  category: { id: string; name: string; slug: string };
  author: string;
  readMinutes?: number | null;
}

// ============================================================
// Glass backdrop — fixed behind all content
// ============================================================
function GlassBackdrop() {
  return (
    <div
      aria-hidden
      className="pointer-events-none fixed inset-0 -z-10 overflow-hidden"
    >
      <div className="absolute -left-40 -top-40 h-[480px] w-[480px] rounded-full bg-[#E5B265]/30 blur-[120px]" />
      <div className="absolute right-[-10%] top-[20%] h-[520px] w-[520px] rounded-full bg-[#110E39]/20 blur-[140px]" />
      <div className="absolute bottom-[-15%] left-[30%] h-[520px] w-[520px] rounded-full bg-[#16a34a]/20 blur-[140px]" />
      <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(250,249,253,0.5)_0%,rgba(243,241,250,0.2)_50%,rgba(250,249,253,0.6)_100%)]" />
      <svg
        className="absolute inset-0 h-full w-full opacity-[0.015]"
        xmlns="http://www.w3.org/2000/svg"
      >
        <filter id="blog-noise">
          <feTurbulence type="fractalNoise" baseFrequency="0.9" />
        </filter>
        <rect width="100%" height="100%" filter="url(#blog-noise)" />
      </svg>
    </div>
  );
}

// ============================================================
// Grid of posts
// ============================================================
function BlogStream({
  posts,
  currentPage,
  totalPages,
  onPageChange,
  lang,
}: {
  posts: BlogPost[];
  currentPage: number;
  totalPages: number;
  onPageChange: (p: number) => void;
  lang: Lang;
}) {
  const { t } = useTranslation();
  if (posts.length === 0) {
    return (
      <div className="rounded-[24px] border border-white/40 bg-white/35 p-10 text-center shadow-[0_8px_30px_rgba(17,14,57,0.06)] backdrop-blur-xl">
        <p className="font-heading text-[18px] font-bold text-[#110E39]">
          {t("blog.no_posts")}
        </p>
        <p className="mt-1 font-sans text-[13px] text-[#525466]">
          {t("blog.no_posts_hint")}
        </p>
      </div>
    );
  }

  return (
    <div>
      {/* Pewter kit #35 (owner, 2026-10-09): a grid of pewter cards, the
          whole card the link. */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-3">
        {posts.map((post) => (
          <article key={post.id} className="min-w-0">
            <BlogCard
              lang={lang}
              slug={post.slug}
              title={post.title}
              image={post.image}
              eyebrow={post.category.name}
              readMinutes={blogReadMinutes(post)}
              className="h-full"
            />
          </article>
        ))}
      </div>

      <div className="mt-10 rounded-full border border-white/40 bg-white/35 p-1.5 shadow-[0_4px_16px_rgba(17,14,57,0.05)] backdrop-blur-xl">
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={onPageChange}
        />
      </div>
    </div>
  );
}

// ============================================================
// Glass sidebar
// ============================================================
function RecentWidget({ initialPosts, lang }: { initialPosts?: BlogPost[]; lang: Lang }) {
  if (initialPosts) {
    return <RecentPostList posts={initialPosts} lang={lang} />;
  }

  const { data: response } = useRecentPosts();
  const posts = response.data;
  return <RecentPostList posts={posts} lang={lang} />;
}

function RecentPostList({ posts, lang }: { posts: BlogPost[]; lang: Lang }) {
  if (!posts || posts.length === 0) return null;
  return (
    <div className="flex flex-col gap-3">
      {posts.slice(0, 5).map((post) => (
        <Link
          key={post.id}
          to="/$lang/blog/$slug"
          params={{ lang, slug: post.slug }}
          className="group flex gap-3 rounded-xl p-2 transition hover:bg-white/50"
        >
          <ImageWithFallback
            src={post.image}
            alt={post.title}
            className="h-14 w-14 flex-shrink-0 rounded-lg object-cover"
          />
          <div>
            <h4 className="font-sans text-[13px] font-semibold leading-tight text-[#110E39] line-clamp-2 group-hover:text-[#E5B265]">
              {post.title}
            </h4>
            <time className="font-sans text-[11px] text-[#666]">
              {post.date}
            </time>
          </div>
        </Link>
      ))}
    </div>
  );
}

function BlogStreamSidebar({
  categories,
  activeCategory,
  onCategoryChange,
  searchValue,
  onSearchChange,
  onSearchSubmit,
  recentPosts,
  lang,
  t,
}: {
  categories: { id: string; label: string; count: number }[];
  activeCategory: string | undefined;
  onCategoryChange: (slug: string | undefined) => void;
  searchValue: string;
  onSearchChange: (v: string) => void;
  onSearchSubmit: (e: React.FormEvent) => void;
  recentPosts?: BlogPost[];
  lang: Lang;
  t: (key: string, opts?: { defaultValue?: string }) => string;
}) {
  return (
    <aside className="flex h-fit flex-col gap-4 lg:sticky lg:top-24">
      <div className="rounded-[24px] border border-white/40 bg-white/35 p-5 shadow-[0_8px_30px_rgba(17,14,57,0.06)] backdrop-blur-xl">
        <p className="font-sans text-[11px] font-bold uppercase tracking-[0.1em] text-[#5a5d77]">
          {t("blog.search", { defaultValue: "Search" })}
        </p>
        <form onSubmit={onSearchSubmit} className="mt-3 flex gap-2">
          <input
            type="text"
            value={searchValue}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={t("blog.search_placeholder", {
              defaultValue: "Search posts…",
            })}
            className="h-10 flex-1 rounded-full border border-white/60 bg-white/60 px-4 font-sans text-[13px] text-[#110E39] outline-none backdrop-blur focus:border-[#110E39]"
          />
          <button
            type="submit"
            className="flex h-10 w-10 items-center justify-center rounded-full bg-[#110E39] navy-depth text-white"
          >
            <Icon name="ri-search-line" />
          </button>
        </form>
      </div>

      {categories.length > 0 && (
        <div className="rounded-[24px] border border-white/40 bg-white/35 p-5 shadow-[0_8px_30px_rgba(17,14,57,0.06)] backdrop-blur-xl">
          <p className="font-sans text-[11px] font-bold uppercase tracking-[0.1em] text-[#5a5d77]">
            {t("blog.categories", { defaultValue: "Categories" })}
          </p>
          <div className="mt-3 flex flex-col gap-2">
            <button
              onClick={() => onCategoryChange(undefined)}
              className={`flex items-center justify-between rounded-xl border px-3 py-2 text-start font-sans text-[13px] font-bold transition ${
                !activeCategory
                  ? "border-[#110E39] bg-[#110E39] navy-depth text-white"
                  : "border-white/50 bg-white/40 text-[#110E39] hover:bg-white/60"
              }`}
            >
              {t("blog.all_categories")}
            </button>
            {categories.map((c) => (
              <button
                key={c.id}
                onClick={() => onCategoryChange(c.id)}
                className={`flex items-center justify-between rounded-xl border px-3 py-2 text-start font-sans text-[13px] font-bold transition ${
                  activeCategory === c.id
                    ? "border-[#110E39] bg-[#110E39] navy-depth text-white"
                    : "border-white/50 bg-white/40 text-[#110E39] hover:bg-white/60"
                }`}
              >
                {c.label}
                <span className="font-sans text-[11px] opacity-70">
                  {c.count}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="rounded-[24px] border border-white/40 bg-white/35 p-5 shadow-[0_8px_30px_rgba(17,14,57,0.06)] backdrop-blur-xl">
        <p className="font-sans text-[11px] font-bold uppercase tracking-[0.1em] text-[#5a5d77]">
          {t("blog.recent_posts", { defaultValue: "Recent posts" })}
        </p>
        <div className="mt-3">
          <Suspense
            fallback={
              <p className="font-sans text-[12px] text-[#666]">{t("common.loading")}</p>
            }
          >
            <RecentWidget initialPosts={recentPosts} lang={lang} />
          </Suspense>
        </div>
      </div>

      <div className="relative overflow-hidden rounded-[24px] border border-white/40 bg-gradient-to-br from-[#E5B265]/40 via-white/30 to-[#110E39]/20 p-5 shadow-[0_8px_30px_rgba(17,14,57,0.08)] backdrop-blur-xl">
        <p className="font-sans text-[11px] font-bold uppercase tracking-[0.1em] text-[#110E39]">
          {t("blog.newsletter_eyebrow")}
        </p>
        <p className="mt-2 font-heading text-[16px] font-bold leading-tight text-[#110E39]">
          {t("blog.newsletter_title")}
        </p>
        <p className="mt-1 font-sans text-[12px] text-[#525466]">
          {t("blog.newsletter_subtitle")}
        </p>
        <form className="mt-3 flex gap-2">
          <input
            type="email"
            placeholder={t("blog.newsletter_email_placeholder")}
            className="h-9 flex-1 rounded-full border border-white/60 bg-white/60 px-3 font-sans text-[12.5px] text-[#110E39] outline-none backdrop-blur focus:border-[#110E39]"
          />
          <button
            type="submit"
            className="rounded-full bg-[#110E39] navy-depth px-4 font-sans text-[12px] font-bold text-white"
          >
            {t("blog.newsletter_join")}
          </button>
        </form>
      </div>
    </aside>
  );
}

// ============================================================
// Main content
// ============================================================
function BlogListContent() {
  const { t } = useTranslation();
  const params = useParams({ strict: false }) as { lang?: string };
  const lang = parseLang(params.lang);
  type BlogListSearch = {
    page?: string | number;
    category?: string;
    search?: string;
  };
  const search = useSearch({ strict: false }) as {
    page?: string;
    category?: string;
    search?: string;
  };
  const navigate = useNavigate();
  const page = Number(search.page) || 1;
  const [searchValue, setSearchValue] = useState(search.search ?? "");

  // Same filters builder as the route loader, so SSR reads the prefetched list.
  const { data: response } = useBlogs(blogListFilters(search));
  const data = response.data;
  const posts = data.items as BlogPost[];
  const recentPosts =
    page === 1 && !search.category && !search.search ? posts.slice(0, 5) : undefined;

  const categories = useMemo(() => {
    const map = new Map<string, { id: string; label: string; count: number }>();
    for (const p of posts) {
      const key = p.category.slug;
      const existing = map.get(key);
      if (existing) existing.count += 1;
      else
        map.set(key, { id: p.category.slug, label: p.category.name, count: 1 });
    }
    return Array.from(map.values());
  }, [posts]);

  const handlePageChange = useCallback(
    (newPage: number) => {
      navigate({
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        search: ((prev: any) => ({ ...prev, page: newPage })) as any,
      } as never);
    },
    [navigate],
  );

  const handleCategoryChange = useCallback(
    (slug: string | undefined) => {
      navigate({
        search: ((prev: BlogListSearch) => {
          const next = { ...prev, page: 1 };
          if (slug) next.category = slug;
          else delete next.category;
          return next;
        }) as never,
      } as never);
    },
    [navigate],
  );

  function handleSearchSubmit(e: React.FormEvent) {
    e.preventDefault();
    navigate({
      search: ((prev: BlogListSearch) => {
        const next = { ...prev, page: 1 };
        if (searchValue.trim()) next.search = searchValue.trim();
        else delete next.search;
        return next;
      }) as never,
    } as never);
  }

  const breadcrumbItems = [
    { name: t("nav.home"), url: siteUrl(lang) },
    { name: t("nav.blog") },
  ];

  const seoTitle = `${t("blog.seo_title")} | MaJourneys`;
  const seoDescription = t("blog.seo_description");
  const blogLd = {
    "@context": "https://schema.org",
    "@type": "Blog",
    name: t("blog.title"),
    description: seoDescription,
    url: `${SITE_ORIGIN}/${lang}/blog`,
    inLanguage: lang,
    publisher: {
      "@type": "Organization",
      name: "MaJourneys",
      url: SITE_ORIGIN,
      logo: `${SITE_ORIGIN}/favicon.svg`,
    },
  };

  return (
    <Container>
      <PageSeo
        title={seoTitle}
        description={seoDescription}
        canonicalPath={`/${lang}/blog`}
        lang={lang}
        ogType="website"
        jsonLd={[blogLd]}
      />
      <BreadcrumbLd items={breadcrumbItems} />
      <div className="grid grid-cols-1 gap-8 pb-24 pt-8 lg:grid-cols-[1fr_320px]">
        <div className="flex flex-col gap-6">
          <div className="rounded-[28px] border border-white/40 bg-white/30 p-8 shadow-[0_12px_40px_rgba(17,14,57,0.08)] backdrop-blur-xl">
            <p className="font-sans text-[11px] font-bold uppercase tracking-[0.12em] text-[#E5B265]">
              {t("blog.eyebrow", { defaultValue: "Travel journal" })}
            </p>
            <h1 className="mt-2 font-heading text-[34px] font-bold leading-tight text-[#110E39] md:text-[44px]">
              {t("blog.title")}
            </h1>
            <p className="mt-3 font-sans text-[14.5px] text-[#525466]">
              {t("blog.description")}
            </p>
          </div>

          <BlogStream
            posts={posts}
            currentPage={data.page}
            totalPages={data.totalPages}
            onPageChange={handlePageChange}
            lang={lang}
          />
        </div>

        <BlogStreamSidebar
          categories={categories}
          activeCategory={search.category}
          onCategoryChange={handleCategoryChange}
          searchValue={searchValue}
          onSearchChange={setSearchValue}
          onSearchSubmit={handleSearchSubmit}
          recentPosts={recentPosts}
          lang={lang}
          t={t}
        />
      </div>
    </Container>
  );
}

function BlogListSkeleton() {
  return (
    <div className="flex items-center justify-center py-32">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#110E39] border-t-transparent" />
    </div>
  );
}

export function BlogListPage() {
  return (
    <main className="relative min-h-screen">
      <GlassBackdrop />
      <Suspense fallback={<BlogListSkeleton />}>
        <BlogListContent />
      </Suspense>
    </main>
  );
}
